import { useState, useCallback } from 'react';
import { Save, PlusCircle } from 'react-feather';
import styled from '@emotion/styled';
import type { QuickChartData, WidgetInstance, WidgetSourceMetadata } from '@trinity/chat';
import { ChartSaveModal } from './ChartSaveModal';
import { WidgetPlacementModal } from './WidgetPlacementModal';

type ChartSaveProps = {
  currentChart: QuickChartData | null;
  chartTitle: string;
  selectedThemeId?: number | null;
  colorOverrides?: Record<string, string> | null;
  labelOverrides?: Record<string, string> | null;
  sourceMetadata?: Omit<WidgetSourceMetadata, 'createdAt'>;
  disabled?: boolean;
};

export const ChartSave = ({
  currentChart,
  chartTitle,
  selectedThemeId,
  colorOverrides,
  labelOverrides,
  sourceMetadata,
  disabled,
}: ChartSaveProps) => {
  // Save Chart Modal State
  const [saveOpen, setSaveOpen] = useState(false);

  // Track if we should proceed to placement after save
  const [shouldShowPlacement, setShouldShowPlacement] = useState(false);

  // Widget Placement Modal State
  const [placementModalOpen, setPlacementModalOpen] = useState(false);
  const [savedWidget, setSavedWidget] = useState<WidgetInstance | null>(null);

  const openSaveModal = useCallback(
    ({ addToDashboard }: { addToDashboard: boolean }) => {
      if (disabled) return;
      setShouldShowPlacement(addToDashboard);
      setSaveOpen(true);
    },
    [disabled]
  );

  const closeSaveModal = useCallback(() => {
    setSaveOpen(false);
  }, []);

  const handleSaveSuccess = useCallback(
    (newWidget: WidgetInstance) => {
      if (shouldShowPlacement) {
        setSavedWidget(newWidget);
        setPlacementModalOpen(true);
      }
      // Reset the flag
      setShouldShowPlacement(false);
    },
    [shouldShowPlacement]
  );

  const closePlacementModal = useCallback(() => {
    setPlacementModalOpen(false);
    setSavedWidget(null);
  }, []);

  const handlePlacementSuccess = useCallback(() => {
    // Placement success is handled by the WidgetPlacementModal itself with a toast
    closePlacementModal();
  }, [closePlacementModal]);

  return (
    <>
      <ButtonContainer>
        <AddWidgetButton onClick={() => openSaveModal({ addToDashboard: false })} disabled={disabled}>
          <StyledIcon />
          <span style={{ marginLeft: 4, fontSize: 16 }}>Save to library</span>
        </AddWidgetButton>

        <AddWidgetButton onClick={() => openSaveModal({ addToDashboard: true })} disabled={disabled}>
          <StyledPlusIcon />
          <span style={{ marginLeft: 4, fontSize: 16 }}>Add to dashboard</span>
        </AddWidgetButton>
      </ButtonContainer>

      {currentChart !== null && (
        <ChartSaveModal
          open={saveOpen}
          onClose={closeSaveModal}
          chartData={currentChart}
          chartTitle={chartTitle}
          selectedThemeId={selectedThemeId}
          colorOverrides={colorOverrides}
          labelOverrides={labelOverrides}
          sourceMetadata={sourceMetadata}
          onSaveSuccess={handleSaveSuccess}
        />
      )}

      {!disabled && savedWidget && (
        <WidgetPlacementModal
          open={placementModalOpen}
          onClose={closePlacementModal}
          widget={savedWidget}
          onPlacementSuccess={handlePlacementSuccess}
        />
      )}
    </>
  );
};

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const AddWidgetButton = styled.div<{ disabled?: boolean }>(({ theme, disabled }) => ({
  cursor: disabled ? 'default' : 'pointer',
  padding: '6px 8px',
  borderRadius: '8px',
  color: theme.palette.wl.enabled ? theme.palette.wl.primary.main : theme.palette.sentiment.primary.main,
  stroke: theme.palette.wl.enabled ? theme.palette.wl.primary.main : theme.palette.sentiment.primary.main,
  backgroundColor: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  ...(disabled && {
    color: theme.palette.gray.light1,
    stroke: theme.palette.gray.light1,
  }),

  ':hover': disabled
    ? {
        backgroundColor: theme.palette.gray.light2,
      }
    : {
        backgroundColor: theme.palette.wl.enabled
          ? theme.palette.btnPrimary.bg
          : theme.palette.sentiment.primary.light,
      },
}));

const StyledIcon = styled(Save)(({ theme }) => ({
  display: 'flex',
  flexShrink: 0,
  color: 'inherit',
}));

const StyledPlusIcon = styled(PlusCircle)(({ theme }) => ({
  display: 'flex',
  flexShrink: 0,
  stroke: 'inherit',
}));
